import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Monitor, Trash2, Copy, RefreshCw, Check, X } from "lucide-react";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

export function TwoFactorPanel() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // {secret, qr_data_url, otpauth_url}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCodes, setShowCodes] = useState(null); // string[]
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const refresh = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setShowCodes(data.recovery_codes);
      setSetup(null);
      setCode("");
      await refresh();
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd, code: disableCode || undefined });
      setDisableOpen(false);
      setDisablePwd("");
      setDisableCode("");
      await refresh();
      toast.success("Two-factor disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const c = window.prompt("Enter your current 2FA code to generate new recovery codes:");
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-codes", { code: c });
      setShowCodes(data.recovery_codes);
      await refresh();
      toast.success("New recovery codes generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <p className="text-sm text-stone-500">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="two-factor-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className={`w-4 h-4 ${status.enabled ? "text-emerald-600" : "text-stone-400"}`} />
            <span className="font-medium" data-testid="two-factor-status">
              {status.enabled ? "Enabled" : "Not enabled"}
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-1">
            {status.enabled
              ? `${status.recovery_codes_remaining} recovery codes remaining. Email-OTP fallback is always available.`
              : "Protect your hive data with an authenticator app (TOTP). Email-OTP fallback is auto-enabled."}
          </p>
        </div>
        {!status.enabled ? (
          <Button onClick={startSetup} disabled={busy} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="two-factor-setup-button">
            <Smartphone className="w-4 h-4 mr-2" /> Set up 2FA
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={regenerate} variant="outline" size="sm" className="rounded-full border-stone-300" data-testid="two-factor-regen-button">
              <RefreshCw className="w-4 h-4 mr-1.5" /> New codes
            </Button>
            <Button onClick={() => setDisableOpen(true)} variant="outline" size="sm" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="two-factor-disable-button">
              <X className="w-4 h-4 mr-1.5" /> Disable
            </Button>
          </div>
        )}
      </div>

      {setup && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 space-y-4" data-testid="two-factor-setup-card">
          <div className="text-sm text-stone-700">
            <strong>1.</strong> Scan this QR with Google Authenticator, Authy, 1Password, or any TOTP app.
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <img src={setup.qr_data_url} alt="2FA QR" className="w-40 h-40 rounded-lg border border-stone-200" data-testid="two-factor-qr" />
            <div className="text-xs text-stone-600 space-y-2 break-all">
              <div>Can't scan? Enter this secret manually:</div>
              <code className="block bg-white border border-stone-200 rounded px-2 py-1 font-mono" data-testid="two-factor-secret">{setup.secret}</code>
            </div>
          </div>
          <div className="text-sm text-stone-700"><strong>2.</strong> Enter the 6-digit code from your app:</div>
          <div className="flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[160px]">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} className="font-mono tracking-[0.4em] text-center text-lg" data-testid="two-factor-confirm-code" />
            </div>
            <Button onClick={enable} disabled={busy || code.length !== 6} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="two-factor-confirm-button">
              <Check className="w-4 h-4 mr-1.5" /> Verify & enable
            </Button>
            <Button onClick={() => { setSetup(null); setCode(""); }} variant="ghost" size="sm" className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}

      <RecoveryCodesDialog codes={showCodes} onClose={() => setShowCodes(null)} />

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-red-700">Disable two-factor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-700">Confirm your password and (optionally) a current authenticator code.</p>
            <div>
              <Label>Password</Label>
              <Input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} data-testid="two-factor-disable-password" />
            </div>
            <div>
              <Label>Authenticator code (optional)</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="two-factor-disable-code" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="destructive" disabled={busy || !disablePwd} onClick={disable} className="rounded-full" data-testid="two-factor-disable-confirm">
              {busy ? "Disabling…" : "Disable two-factor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecoveryCodesDialog({ codes, onClose }) {
  if (!codes) return null;
  const text = codes.join("\n");
  const copy = () => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };
  const download = () => {
    const blob = new Blob([`Hive @ Buzzzillion — recovery codes\n${new Date().toISOString()}\n\n${text}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "hive-recovery-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">Recovery codes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3" data-testid="recovery-codes-dialog">
          <p className="text-sm text-stone-700">
            Save these somewhere safe. Each code works once if you lose access to your authenticator.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-stone-50 border border-stone-200 rounded-lg p-4">
            {codes.map((c) => <div key={c} data-testid={`recovery-code-${c}`}>{c}</div>)}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} className="rounded-full border-stone-300"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
          <Button onClick={download} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300">Download .txt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Sign out this session?")) return;
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success("Session revoked");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Sign out every other device?")) return;
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(`Revoked ${data.revoked} session${data.revoked === 1 ? "" : "s"}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="sessions-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-stone-600">
          {loading ? "Loading…" : `${sessions.length} active session${sessions.length === 1 ? "" : "s"}.`}
        </p>
        {sessions.length > 1 && (
          <Button variant="outline" size="sm" onClick={revokeOthers} className="rounded-full border-stone-300" data-testid="revoke-others-button">
            Sign out other devices
          </Button>
        )}
      </div>
      <ul className="space-y-2">
        {sessions.map((s) => (
          <li key={s.id} className={`rounded-xl border ${s.is_current ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"} p-4 flex items-center justify-between gap-3 flex-wrap`} data-testid={`session-row-${s.id}`}>
            <div className="flex items-center gap-3">
              <Monitor className="w-5 h-5 text-stone-500" />
              <div>
                <div className="font-medium text-sm flex items-center gap-2">
                  {s.device}
                  {s.is_current && <span className="text-[10px] uppercase tracking-[0.15em] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">This device</span>}
                </div>
                <div className="text-xs text-stone-500">IP {s.ip || "unknown"} · last active {formatRelative(s.last_active)}</div>
              </div>
            </div>
            {!s.is_current && (
              <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} className="text-red-700 hover:bg-red-50 rounded-full" data-testid={`revoke-session-${s.id}`}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Revoke
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TrustedDevicesPanel() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/trusted-devices");
      setDevices(data);
    } catch {
      // panel renders empty on failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Stop trusting this device? 2FA will be required next time you sign in from it.")) return;
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success("Trusted device revoked");
      // if the revoked one is the current device, drop the stored token
      const stored = localStorage.getItem("beekeep_trusted_device_token");
      if (stored && stored.startsWith(`${id}.`)) {
        localStorage.removeItem("beekeep_trusted_device_token");
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const revokeAll = async () => {
    if (!window.confirm("Stop trusting every device? 2FA will be required on every sign in.")) return;
    try {
      const { data } = await api.post("/auth/trusted-devices/revoke-all");
      localStorage.removeItem("beekeep_trusted_device_token");
      toast.success(`Revoked ${data.revoked} trusted device${data.revoked === 1 ? "" : "s"}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="trusted-devices-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-stone-600 max-w-md">
          {loading
            ? "Loading…"
            : devices.length === 0
              ? "No trusted devices. After completing 2FA, you can choose to skip the challenge on future sign-ins from that device."
              : `${devices.length} trusted device${devices.length === 1 ? "" : "s"} — each expires 90 days after first trusted.`}
        </p>
        {devices.length > 0 && (
          <Button variant="outline" size="sm" onClick={revokeAll} className="rounded-full border-stone-300" data-testid="trusted-devices-revoke-all">
            Revoke all
          </Button>
        )}
      </div>
      <ul className="space-y-2">
        {devices.map((d) => (
          <li
            key={d.id}
            className={`rounded-xl border ${d.is_current ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"} p-4 flex items-center justify-between gap-3 flex-wrap`}
            data-testid={`trusted-device-row-${d.id}`}
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-stone-500" />
              <div>
                <div className="font-medium text-sm flex items-center gap-2">
                  {d.device}
                  {d.is_current && (
                    <span className="text-[10px] uppercase tracking-[0.15em] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">This device</span>
                  )}
                </div>
                <div className="text-xs text-stone-500">
                  IP {d.ip || "unknown"} · last used {formatRelative(d.last_used_at)} · expires {new Date(d.expires_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => revoke(d.id)}
              className="text-red-700 hover:bg-red-50 rounded-full"
              data-testid={`trusted-device-revoke-${d.id}`}
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Revoke
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}



export function LoginHistoryPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/login-history")
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-stone-500">Loading…</p>;
  if (logs.length === 0) return <p className="text-sm text-stone-500">No activity yet.</p>;

  return (
    <div className="rounded-xl border border-stone-200 overflow-hidden" data-testid="login-history-panel">
      <ul className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
        {logs.map((l) => (
          <li key={l.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`login-log-${l.id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              {l.success ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <X className="w-4 h-4 text-red-500 shrink-0" />}
              <div className="min-w-0">
                <div className="truncate">{l.device}</div>
                <div className="text-xs text-stone-500">IP {l.ip || "unknown"} · {l.reason}</div>
              </div>
            </div>
            <div className="text-xs text-stone-500 shrink-0">{formatRelative(l.at)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
